import { Component, signal, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { ToastHost } from './ui/toast-host';
import { AuthService } from '@services/auth.service';
import { PageStatusService } from '@services/page-status.service';
import { paths } from './pages/paths';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ToastHost],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly title = signal('Lizzie & Jon');
  protected readonly mobileMenuOpen = signal(false);
  protected readonly auth = inject(AuthService);
  private readonly pages = inject(PageStatusService).pages;

  protected readonly navLinks = computed(() => {
    const effectiveRole = this.auth.role() || 'public';
    const pages = this.pages.value();

    const baseVisible = pages.filter((p) => {
      if (effectiveRole === 'admin') return p.status !== 'hidden';
      return p.status === 'public' || (p.status === 'guest' && effectiveRole === 'guest');
    });

    const rootVisible = (
      effectiveRole == 'public'
        ? paths.filter((p) => p.publicOnly)
        : paths.filter((p) => p.loggedInOnly)
    ).map((p) => ({ path: p.path, title: p.label, status: 'public', path_slug: null }));

    const visible = [...baseVisible, ...rootVisible];
    const withLabels = visible.map((p) => {
      const page = pages?.find((pg) => pg.path === p.path);
      // Admin-only either statically (paths.ts) or dynamically (page marked admin-only via Site Admin).
      const isAdminOnly = effectiveRole === 'admin' && page?.status === 'admin';
      return { path: p.path, label: p.title, isAdminOnly };
    });

    const top = withLabels.filter((link) => !link.isAdminOnly);
    const admin = withLabels.filter((link) => link.isAdminOnly);
    return {
      top,
      admin,
    };
  });

  protected toggleMenu(): void {
    this.mobileMenuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.mobileMenuOpen.set(false);
  }
}
